
import { DateField } from "@/components/form/input/DateField";
import TextField from "@/components/form/input/TextField";
import { TimePickerField } from "@/components/form/input/TimePickerField";
import { FixedJobSearchField } from "@/components/form/search-fixed-job";
import { TeacherSearchField } from "@/components/form/search-teacher";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { FixedJob } from "@/hooks/useFixedJobSearch";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

type Input = {
  teacherId: string;
  date: string;
  startTime: string;
  endTime: string;
  fixedJob: FixedJob;
  classType: string;
  subject: string;
  students: string;
  student_codes: string;
  educationLevel: string;
  branch: string;
  language: string;
}

export default function SpecialClassForm() {
  const { control, setValue, handleSubmit, register, watch, reset } = useForm<Input>({
    defaultValues: {
      teacherId: "",
      date: "",
      startTime: "",
      endTime: "",
      classType: "",
      subject: "",
      students: "",
      student_codes: "",
      educationLevel: "",
      branch: "",
      language: ""
    }
  });

  const watchFixedJob = watch("fixedJob");

  const { profile } = useProfile();

  const onSubmit = async (data: Input) => {
    console.log(data);
    if (!data.date || !data.startTime || !data.endTime || !data.fixedJob.time_slot_id || !data.teacherId || !data.student_codes) {
      toast({
        title: "Error",
        description: "กรุณากรอกข้อมูลให้ครบถ้วน",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create a unique job_id for this special class
      const currentDate = new Date().getTime();
      const specialJobId = `special-${currentDate}`;
      
      // Insert into job_students table
      const { data: jobData, error: jobError } = await supabase.from("job_students").insert({
        student_id: data.student_codes,
        fixed_job_id: data.fixedJob.id,
        is_special_job: true,
        class_program: 'TEMPORARY',
        status: "WAITING",
        approve_status: "pending",
        job_id: parseInt(specialJobId) // Ensure it's a number as required by the schema
      })
      .select();
      
      if (jobError) {
        console.log(jobError);
        toast({
          title: "Error",
          description: "เกิดข้อผิดพลาดในการสร้างงาน",
          variant: "destructive",
        });
        return;
      }

      // Split student codes and insert each one
      const studentCodes = data.student_codes.split(",").map((code) => code.trim());
      
      // Prepare the data for batch insert - ensure job_id is a number
      const jobStudents = studentCodes.map((code) => ({
        job_id: parseInt(specialJobId), // Convert to number as required by the schema
        student_id: code,
        is_special_job: true,
        class_program: 'TEMPORARY',
        approve_status: "pending",
        fixed_job_id: data.fixedJob.id
      }));

      const { error: jobStudentError } = await supabase.from("job_students").insert(jobStudents);

      if (jobStudentError) {
        console.log(jobStudentError);
        toast({
          title: "Error",
          description: "เกิดข้อผิดพลาดในการสร้างงาน",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Special class created successfully",
      });
      reset();
    } catch (error) {
      console.error("Error creating special class:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (profile) {
      setValue("teacherId", profile.teacher_id);
    }
  }, [profile, setValue]);

  const mapCourseType = (courseType: string) => {
    switch (courseType) {
      case "REGULAR":
        return "ปกติ";
      case "INTER":
        return "Inter";
      default:
        return "";
    }
  }

  useEffect(() => {
    if (watchFixedJob) {
      setValue("students", watchFixedJob.student_names);
      setValue("student_codes", watchFixedJob.student_codes);
      setValue("subject", watchFixedJob.subjects);
      setValue("educationLevel", watchFixedJob.education_level);
      setValue("branch", watchFixedJob.branch);
      setValue("language", watchFixedJob.language);
      setValue("classType", `${watchFixedJob.class_type} ${mapCourseType(watchFixedJob.course_type)}`);
    }
  }, [watchFixedJob, setValue]);

  return (
    <form className="flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
      <FixedJobSearchField control={control} setValue={setValue} name="fixedJob" targetField="fixedJob" teacherCode={profile?.teacher_id} />

      <TeacherSearchField disabled control={control} setValue={setValue} name="teacherId" targetField="teacherId" teacherCode={profile?.teacher_id} />

      <TextField readOnly name="students" field={{ id: "students", name: "students", type: "text", label: "นักเรียน" }} register={register} />
      <TextField readOnly name="classType" field={{ id: "classType", name: "classType", type: "text", label: "ประเภทคลาส" }} register={register} />
      <TextField readOnly name="subject" field={{ id: "subject", name: "subject", type: "text", label: "วิชา" }} register={register} />
      <TextField readOnly name="educationLevel" field={{ id: "educationLevel", name: "educationLevel", type: "text", label: "ระดับชั้น" }} register={register} />
      <TextField readOnly name="branch" field={{ id: "branch", name: "branch", type: "text", label: "สาขา" }} register={register} />
      <TextField readOnly name="language" field={{ id: "language", name: "language", type: "text", label: "ภาษา" }} register={register} />

      <DateField control={control} setValue={setValue} name="date" field={{ id: "date", name: "date", type: "date", label: "วันที่เรียนเฉพาะกิจ" }} />
      <div className="flex flex-col w-full gap-2">
        <TimePickerField control={control} setValue={setValue} name="startTime" field={{ id: "startTime", name: "startTime", type: "time", label: "เวลาเริ่มต้น" }} />
        <TimePickerField control={control} setValue={setValue} name="endTime" field={{ id: "endTime", name: "endTime", type: "time", label: "เวลาสิ้นสุด" }} />
      </div>
      <Button type="submit">ยืนยัน</Button>
    </form>
  );
}
